import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, Chip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Divider,
  List, ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction,
  Checkbox, Switch, FormControlLabel, useTheme
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  DragIndicator as DragIcon, Visibility as ViewIcon
} from '@mui/icons-material';
import internalServiceService from '../../services/internal-service.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { can } from '../../utils/roleUtils';

// ─── Group Edit Dialog ───────────────────────────────────────────────────────

const GroupDialog = ({ open, onClose, onSave, group, allServices }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState([]); // [{serviceId, required, serviceName}]
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (group) {
        setName(group.name || '');
        setDescription(group.description || '');
        setItems((group.items || []).map(i => ({
          serviceId: i.serviceId,
          required: i.required !== false,
          serviceName: i.serviceName || '',
          serviceCode: i.serviceCode || ''
        })));
      } else {
        setName('');
        setDescription('');
        setItems([]);
      }
    }
  }, [open, group]);

  const selectedIds = new Set(items.map(i => i.serviceId));

  const toggleService = (svc) => {
    if (selectedIds.has(svc.id)) {
      setItems(prev => prev.filter(i => i.serviceId !== svc.id));
    } else {
      setItems(prev => [...prev, {
        serviceId: svc.id,
        required: true,
        serviceName: svc.name,
        serviceCode: svc.code
      }]);
    }
  };

  const toggleRequired = (serviceId) => {
    setItems(prev => prev.map(i =>
      i.serviceId === serviceId ? { ...i, required: !i.required } : i
    ));
  };

  const moveItem = (index, direction) => {
    const newItems = [...items];
    const target = index + direction;
    if (target < 0 || target >= newItems.length) return;
    [newItems[index], newItems[target]] = [newItems[target], newItems[index]];
    setItems(newItems);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || null,
        items: items.map((item, idx) => ({
          serviceId: item.serviceId,
          sequence: idx,
          required: item.required
        }))
      });
      onClose();
    } catch (err) {
      console.error('Error saving group:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{group ? 'Edit Service Group' : 'Create Service Group'}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth label="Group Name" value={name}
          onChange={e => setName(e.target.value)}
          sx={{ mt: 1, mb: 2 }} required size="small"
        />
        <TextField
          fullWidth label="Description" value={description}
          onChange={e => setDescription(e.target.value)}
          sx={{ mb: 2 }} size="small" multiline rows={2}
        />

        <Divider sx={{ mb: 1 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Services in group ({items.length} selected)
        </Typography>

        {/* Selected items with ordering */}
        {items.length > 0 && (
          <Paper variant="outlined" sx={{ mb: 2, maxHeight: 180, overflow: 'auto' }}>
            <List dense>
              {items.map((item, idx) => (
                <ListItem key={item.serviceId} sx={{ py: 0.5 }}>
                  <Box display="flex" flexDirection="column" mr={1}>
                    <IconButton size="small" onClick={() => moveItem(idx, -1)} disabled={idx === 0}>
                      <Typography variant="caption">▲</Typography>
                    </IconButton>
                    <IconButton size="small" onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1}>
                      <Typography variant="caption">▼</Typography>
                    </IconButton>
                  </Box>
                  <ListItemText
                    primary={<Typography variant="body2">{idx + 1}. {item.serviceName}</Typography>}
                    secondary={item.serviceCode}
                  />
                  <ListItemSecondaryAction>
                    <FormControlLabel
                      control={<Switch size="small" checked={item.required} onChange={() => toggleRequired(item.serviceId)} />}
                      label={<Typography variant="caption">{item.required ? 'Required' : 'Optional'}</Typography>}
                    />
                    <IconButton size="small" onClick={() => toggleService({ id: item.serviceId })}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {/* Available services to add */}
        <Typography variant="caption" color="text.secondary">
          Click to add/remove:
        </Typography>
        <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
          <List dense>
            {allServices.map(svc => (
              <ListItem key={svc.id} disablePadding>
                <ListItemIcon sx={{ minWidth: 32, pl: 1 }}>
                  <Checkbox
                    size="small" checked={selectedIds.has(svc.id)}
                    onChange={() => toggleService(svc)}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography variant="body2">{svc.name}</Typography>}
                  secondary={`${svc.category || ''} ${svc.code || ''}`}
                  onClick={() => toggleService(svc)}
                  sx={{ cursor: 'pointer' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !name.trim()}>
          {saving ? 'Saving...' : (group ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Group View (read-only ordered sequence) ─────────────────────────────────

const GroupViewDialog = ({ open, onClose, group }) => {
  const ordered = [...((group && group.items) || [])].sort(
    (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {group?.name || 'Service Group'}
        {group?.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {group.description}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Services are carried out in this order.
        </Typography>

        {ordered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No services in this group.
          </Typography>
        ) : (
          ordered.map((item, idx) => {
            const isLast = idx === ordered.length - 1;
            return (
              <Box key={item.id || item.serviceId || idx} sx={{ display: 'flex', gap: 1.5 }}>
                {/* Step marker + connector to the next step */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 28, height: 28, flexShrink: 0, borderRadius: '50%',
                      bgcolor: 'secondary.main', color: 'secondary.contrastText',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.8rem', fontWeight: 700,
                    }}
                  >
                    {idx + 1}
                  </Box>
                  {!isLast && <Box sx={{ width: '2px', flexGrow: 1, minHeight: 20, bgcolor: 'divider', my: 0.5 }} />}
                </Box>

                {/* Step card */}
                <Paper
                  variant="outlined"
                  sx={{ flex: 1, p: 1.25, mb: isLast ? 0 : 1.5, borderRadius: 1.5 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {item.serviceName || item.serviceCode || 'Service'}
                    </Typography>
                    <Chip
                      label={item.required !== false ? 'Required' : 'Optional'}
                      size="small"
                      color={item.required !== false ? 'primary' : 'default'}
                      variant={item.required !== false ? 'filled' : 'outlined'}
                      sx={{ fontSize: '0.62rem', height: 18 }}
                    />
                  </Box>
                  {item.serviceCode && (
                    <Typography variant="caption" color="text.secondary">{item.serviceCode}</Typography>
                  )}
                </Paper>
              </Box>
            );
          })
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const ServiceGroups = () => {
  // Session user for permission checks (this page receives no currentUser prop).
  const sessionUser = (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
  })();
  const theme = useTheme();
  const [groups, setGroups] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingGroup, setViewingGroup] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [grpList, svcList] = await Promise.all([
        internalServiceService.getServiceGroups(),
        internalServiceService.getInternalServices()
      ]);
      setGroups(grpList);
      setServices(svcList);
    } catch (err) {
      console.error('Error loading service groups:', err);
      setError('Failed to load service groups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingGroup(null);
    setDialogOpen(true);
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setDialogOpen(true);
  };

  const handleView = (group) => {
    setViewingGroup(group);
    setViewDialogOpen(true);
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Delete group "${group.name}"? This cannot be undone.`)) return;
    try {
      await internalServiceService.deleteServiceGroup(group.id);
      setSuccess('Group deleted');
      loadData();
    } catch (err) {
      setError('Failed to delete group');
    }
  };

  const handleSave = async (data) => {
    if (editingGroup) {
      await internalServiceService.updateServiceGroup(editingGroup.id, data);
      setSuccess('Group updated');
    } else {
      await internalServiceService.createServiceGroup(data);
      setSuccess('Group created');
    }
    loadData();
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          Service Groups ({groups.length})
        </Typography>
        <Box display="flex" gap={1}>
          {can(sessionUser, 'service_catalog', 'write') && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleCreate}>
              Create Group
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={getBrandedTableHeadSx(theme)}>
            <TableRow sx={{ '& th': { fontSize: '0.75rem', fontWeight: 600, py: 0.75 } }}>
              <TableCell>Group</TableCell>
              <TableCell>Services</TableCell>
              <TableCell align="center">Items</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No service groups yet. Create one or import from the UAE template.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              groups.map(group => (
                <TableRow key={group.id} hover>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.8125rem' }}>{group.name}</Typography>
                    {group.description && (
                      <Typography variant="caption" color="text.secondary">{group.description}</Typography>
                    )}
                    {group.templateKey && (
                      <Chip label="Template" size="small" sx={{ ml: 1, fontSize: '0.65rem' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {(group.items || []).map((item, idx) => (
                        <Chip
                          key={item.id || idx}
                          label={item.serviceName}
                          size="small"
                          color={item.required ? 'primary' : 'default'}
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{group.itemCount || 0}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={group.status}
                      size="small"
                      color={group.status === 'ACTIVE' ? 'success' : 'default'}
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <Tooltip title="View order">
                        <IconButton size="small" onClick={() => handleView(group)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {can(sessionUser, 'service_catalog', 'write') && (
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(group)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {can(sessionUser, 'service_catalog', 'delete') && (
                        <Tooltip title="Delete">
                          <IconButton size="small" sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }} onClick={() => handleDelete(group)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <GroupDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        group={editingGroup}
        allServices={services}
      />

      <GroupViewDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        group={viewingGroup}
      />
    </Box>
  );
};

export default ServiceGroups;
